import { keyBy } from 'lodash';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import { Page } from '~/models/page.model';
import {
    IContainerBlockResponse,
    IMember,
    IVariableResponse,
    TBlockResponse
} from '~/types/server.types';
import { FlowConfigurationModel } from '~/models/flow-configuration.model';
import { Flow } from '~/models/flow.model';
import { TBlock } from '~/types/builder.types';
import { ContainerBlock } from '~/models/blocks/container-block.model';
import { IFlow } from '~/types/model.types';
import { RootStore } from '~/stores/root.store';
import { FlowRevision } from '~/models/flow-revision.model';

export class FlowComponentsCXCtrl {
    rootStore: CXRootStore;

    constructor(rootStore: CXRootStore) {
        this.rootStore = rootStore;
    }

    loadFlow(
        flow: IFlow,
        rootStore: RootStore,
        revision?: FlowRevision<RootStore>
    ) {
        return Promise.resolve(this.load({ flow, rootStore, revision }));
    }

    loadPage(flow: IFlow | Flow, page: Page) {
        this.load({
            flow,
            page,
            baseBlocks: [...page.blocks],
            rootStore: page.rootStore
        });
    }

    loadBlock(flow: IFlow, block: TBlock) {
        const page = new Page(block.page, block.flowRevision, block.rootStore);
        const rootBlock = new ContainerBlock(
            block.page.rootBlock as IContainerBlockResponse,
            block.flowRevision,
            block.rootStore
        );

        rootBlock.children = rootBlock.children.filter(
            x => x.block_id === block.block_id
        );

        this.load({
            flow,
            page,
            baseBlocks: [rootBlock, ...rootBlock.allDescendants()],
            rootStore: page.rootStore
        });
    }

    private load({
        flow,
        page,
        baseBlocks,
        rootStore,
        revision
    }: {
        flow: IFlow | Flow;
        page?: Page;
        baseBlocks?: TBlock[];
        rootStore: RootStore;
        revision?: FlowRevision<RootStore>;
    }) {
        if (!flow) {
            return;
        }

        const {
            flowViewUiStore: { selectPage }
        } = this.rootStore;

        const manipulatedFlow = this.cloneFlowAndApplyRevision({
            flow,
            rootStore,
            revision
        });

        const pageToSelect = this.loadFlowAsCXFlow({
            flow: manipulatedFlow,
            page,
            blocks: baseBlocks
        });

        this.addRequiredCXFlowData(page?.page_id);

        selectPage(pageToSelect.page_id);
    }

    private loadFlowAsCXFlow({
        flow,
        page,
        blocks
    }: {
        flow: Flow;
        page?: Page;
        blocks?: TBlock[];
    }): Page {
        const { flowStore } = this.rootStore;

        if (page) {
            const pageBlocks = this.getAdditionalPageBlocks(page, blocks);

            flowStore.load(
                flow,
                { [page.page_id]: page },
                pageBlocks,
                this.extractBlocksVariables(flow, pageBlocks)
            );

            return page;
        }

        const pagesMap = keyBy(flow.pages, 'page_id');
        const blocksMap = keyBy(flow.blocks, 'block_id') as Record<
            string,
            TBlockResponse
        >;

        flowStore.load(
            flow,
            pagesMap,
            blocksMap,
            this.extractBlocksVariables(flow, blocksMap)
        );

        return flow.pages[0];
    }

    private getAdditionalPageBlocks(page: Page, baseBlocks: Array<TBlock>) {
        let blocks = baseBlocks;

        if (page.isInvoicePage) {
            blocks = [...blocks, ...page.flowRevision.chargeBlock.page.blocks];
        }

        if (page.isPaymentPage) {
            blocks = [
                ...blocks,
                ...page.flowRevision.paymentsBlocks,
                ...page.flowRevision.invoiceContainerBlock.page.blocks
            ];
        }

        const pageBlocks = keyBy(blocks, 'block_id') as Record<
            string,
            TBlockResponse
        >;

        return pageBlocks;
    }

    private cloneFlowAndApplyRevision({
        flow,
        rootStore,
        revision
    }: {
        flow: IFlow;
        rootStore: RootStore;
        revision?: FlowRevision<RootStore>;
    }) {
        const manipulatedFlow = new Flow(flow, rootStore);
        const manipulatedFlowRevision =
            manipulatedFlow.activeRevision || manipulatedFlow.draftRevision;

        manipulatedFlow.draftRevision = revision || manipulatedFlowRevision;

        (manipulatedFlow as IFlow).revision_id =
            revision?._id || manipulatedFlowRevision?._id;

        return manipulatedFlow;
    }

    private extractBlocksVariables(
        flow: IFlow,
        blocksHash: Record<string, any>
    ) {
        const blocksVariables = flow.variables.filter(
            ({ connected_block_id }) => blocksHash[connected_block_id]
        );

        return keyBy(blocksVariables, 'variable_id') as Record<
            string,
            IVariableResponse
        >;
    }

    private addRequiredCXFlowData(pageId?: string) {
        const { flowStore } = this.rootStore;
        const { flow } = flowStore;

        flow.members = flow.members?.length
            ? flow.members
            : this.createPlaceholderMembers();

        flow.configuration = this.createFakeConfiguration(flow.configuration);

        if (pageId) {
            Object.values(flowStore.blocks).forEach(block => {
                block.page_id = pageId;
            });
        }
    }

    private createPlaceholderMembers() {
        const { userStore } = this.rootStore;

        return [{ ...userStore.currUser }] as Array<IMember>;
    }

    private createFakeConfiguration(configuration: FlowConfigurationModel) {
        if (!configuration) {
            return new FlowConfigurationModel({
                auto_expiration: {}
            });
        }

        if (!configuration.auto_expiration) {
            configuration.auto_expiration = {};
        }

        return configuration;
    }
}
